/**
 * Phone normalization for form-input → backend-DTO consistency.
 *
 * Backend phone fields (e.g. `Driver.mobileNumber`, `CustomerContact.phone`)
 * accept digits-only strings (regex `^[0-9]{10,20}$`). Frontend forms accept
 * formatted user input — `(713) 555-0100`, `+1 713-555-0100`, `+90 532 123 45 67`.
 * Without normalization, the form schema's regex rejects formatted input
 * silently; with normalization, the regex sees the digits-only canonical form
 * and the user keeps their preferred display format.
 *
 * Use as a `z.preprocess` step in form schemas:
 *   `mobileNumber: z.preprocess(stripNonDigits, z.string().regex(PHONE_DIGITS_REGEX))`
 *
 * @module @axon/common/utils/normalize-phone
 */

/**
 * Strip every non-digit character. Returns the input unchanged if it isn't
 * a string — preserves the type so a downstream zod check can produce a
 * type-mismatch error instead of silently coercing.
 *
 * Used as a `z.preprocess` callback before phone-shape regex validation.
 */
export function stripNonDigits(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return value.replace(/\D/g, '');
}

/**
 * Canonical phone-shape regex — 10-20 digits. Matches the backend
 * `class-validator` `@Matches(/^[0-9]{10,20}$/)` decorator surface used
 * across `Driver.mobileNumber`, `CustomerContact.phone`, and similar.
 *
 * Lower bound of 10 covers the shortest legitimate carrier-issued mobile
 * (US/CA without country code, TR without `90` prefix). Upper bound of 20
 * is generous for international formats that include country code +
 * extension digits.
 */
export const PHONE_DIGITS_REGEX = /^[0-9]{10,20}$/;

/**
 * Convenience wrapper for callers that want a direct `string -> string`
 * normalization (e.g. CSV ingest, API client middleware) without going
 * through zod. Returns empty string for non-string input.
 */
export function normalizePhoneToDigits(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\D/g, '');
}
